import React, { useState, useEffect } from 'react';
import { fetchData, fetchDairyDetails, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Select from 'react-select';
import Button from '@mui/material/Button';
import { TableContainer, Table, TableBody, TableRow, TableCell, Paper, TableHead } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import CustomCalendar from './Calendar';
import axios from 'axios';

function DoctorMontlyDiary() {
    // const history = useHistory();
    const [daysOfMonth, setDaysOfMonth] = useState([]);
    const [staffOptions, setStaffOptions] = useState([]);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [staffId, setStaffId] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const { isAuthenticated, companyId, token, userId, logout } = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
    }, [isAuthenticated, token, logout]);

    useEffect(() => {
        const date = new Date();
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        setFromDate(firstDay);
        setToDate(lastDay);
    }, []);

    useEffect(() => {
        fetchStaff();
    }, []);

    const fetchStaff = async () => {
        try {
            const response = await fetchData('DoctorApi', `/${companyId}`);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(doctor => ({
                    value: doctor.Id,
                    label: doctor.Name,
                }));
                setStaffOptions(options);
            }
        } catch (error) {
            console.error('Error fetching doctor:', error);
        }
    };

    const handleStaffChange = async (selectedOption) => {
        setSelectedStaff(selectedOption);
        const formattedFromDate = formatDate(fromDate);
        const formattedToDate = formatDate(toDate);
        setIsLoading(true);
        setStaffId(selectedOption.value);
        try {
            const response = await fetchDairyDetails('DoctorDiaryApi', formattedFromDate, formattedToDate, selectedOption.value, companyId, userId, token);
            if (response.responseCode === '1111' && response.response_data) {
                console.log('First item from response:', response.response_data[0]);
                setDaysOfMonth(response.response_data);
            } else {
                console.error('Invalid response format', response);
            }
        } catch (error) {
            console.error('Error fetching days of the week:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatDate = (date) => {
        const d = new Date(date);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const day = d.getDate().toString().padStart(2, '0');
        const year = d.getFullYear();
        return `${year}-${month}-${day}`;
    };

    const dayColors = {
        Sunday: 'red',
        Monday: 'darkgreen',
        Tuesday: 'darkgreen',
        Wednesday: 'darkgreen',
        Thursday: 'darkgreen',
        Friday: 'darkgreen',
        Saturday: 'darkred'
    };

    const getDayName = (dateString) => {
        const date = new Date(dateString);
        const options = { weekday: 'long' };
        return date.toLocaleDateString('en-US', options);
    };

    const CustomTableBody = ({ daysOfMonth, setDaysOfMonth }) => {
        const handleCheckboxChange = (id) => {
            setDaysOfMonth(prevDaysOfMonth =>
                prevDaysOfMonth.map(day =>
                    day.Id === id ? { ...day, IsAvailable: !day.IsAvailable } : day
                )
            );
        };

        const handleUpdate = async (event) => {
            event.preventDefault();
            setIsLoading(true);

            // Prepare the updated schedule data
            const updatedScheduleData = {
                DoctorAvailability: daysOfMonth.map(item => ({
                    ...item,
                    CompanyId: companyId,
                    ModifiedBy: userId,
                })),
            };

            try {
                const response = await updateData(
                    'DoctorDiaryApi',
                    staffId,
                    updatedScheduleData,
                    token
                );

                console.log('Record updated successfully:', response);
                setSuccessMessage("Doctor Diary Updated successfully!");
                setErrorMessage("");
                setTimeout(() => {
                    setSuccessMessage("");
                    window.location.reload();
                }, 3000);
            } catch (error) {
                console.error('Schedule Update error:', error);
                setErrorMessage("Failed to update the schedule. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                padding: '20px',
                minHeight: '100vh'
            }}>
                <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                    {/* Header Card */}
                    <Paper elevation={3} style={{
                        padding: '8px 20px',
                        marginBottom: '20px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        marginTop: '-50px',
                        height: '50px',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: '15px' }}>
                            <h5 style={{
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '14px',
                                margin: 0,
                                letterSpacing: '0.5px',
                                whiteSpace: 'nowrap'
                            }}>
                                Doctor Diary Management
                            </h5>

                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <div>
                                    <input
                                        type="date"
                                        value={fromDate ? formatDate(fromDate) : ''}
                                        onChange={(e) => setFromDate(new Date(e.target.value))}
                                        style={{
                                            padding: '6px 10px',
                                            fontSize: '12px',
                                            border: 'none',
                                            borderRadius: '5px',
                                            outline: 'none',
                                            width: '130px',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                                <div>
                                    <input
                                        type="date"
                                        value={toDate ? formatDate(toDate) : ''}
                                        onChange={(e) => setToDate(new Date(e.target.value))}
                                        style={{
                                            padding: '6px 10px',
                                            fontSize: '12px',
                                            border: 'none',
                                            borderRadius: '5px',
                                            outline: 'none',
                                            width: '130px',
                                            height: '29px'
                                        }}
                                    />
                                </div>
                                <div style={{ width: '220px' }}>
                                    <Select
                                        value={selectedStaff}
                                        onChange={handleStaffChange}
                                        options={staffOptions}
                                        placeholder="Choose doctor..."
                                        isSearchable
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '29px',
                                                height: '29px',
                                                borderRadius: '5px',
                                                border: 'none',
                                                fontSize: '12px'
                                            }),
                                            valueContainer: (base) => ({
                                                ...base,
                                                height: '29px',
                                                padding: '0 8px'
                                            }),
                                            input: (base) => ({
                                                ...base,
                                                margin: '0',
                                                padding: '0'
                                            }),
                                            indicatorsContainer: (base) => ({
                                                ...base,
                                                height: '29px'
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </Paper>

                    {/* Main Content - Full Width */}
                    <Paper elevation={2} style={{
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}>
                        <div style={{
                            padding: '10px 15px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '14px',
                                fontWeight: '600',
                                color: 'white'
                            }}>
                                Daily Availability
                            </h3>
                            {daysOfMonth.length > 0 && (
                                <Button
                                    onClick={handleUpdate}
                                    variant="contained"
                                    style={{
                                        backgroundColor: 'white',
                                        color: '#667eea',
                                        fontWeight: '600',
                                        textTransform: 'none',
                                        padding: '5px 16px',
                                        borderRadius: '5px',
                                        fontSize: '12px',
                                        height: '29px'
                                    }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? <CircularProgress size={16} style={{ color: '#667eea' }} /> : "Save Changes"}
                                </Button>
                            )}
                        </div>

                        <div style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', padding: '8px' }}>
                            {Array.isArray(daysOfMonth) && daysOfMonth.length > 0 ? (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                    gap: '6px'
                                }}>
                                    {daysOfMonth.map((item, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '8px 10px',
                                                backgroundColor: item.IsAvailable ? '#f0f9ff' : '#fff',
                                                border: item.IsAvailable ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                                                borderRadius: '6px',
                                                transition: 'all 0.2s ease',
                                                cursor: 'pointer',
                                                height: '45px'
                                            }}
                                            onClick={() => handleCheckboxChange(item.Id)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '5px',
                                                    backgroundColor: dayColors[getDayName(item.AvailableDate)],
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'white',
                                                    fontWeight: 'bold'
                                                }}>
                                                    <span style={{ fontSize: '8px' }}>
                                                        {getDayName(item.AvailableDate).substring(0, 3).toUpperCase()}
                                                    </span>
                                                    <span style={{ fontSize: '12px' }}>
                                                        {new Date(item.AvailableDate).getDate()}
                                                    </span>
                                                </div>
                                                <div>
                                                    <div style={{
                                                        fontSize: '12px',
                                                        fontWeight: '600',
                                                        color: '#1f2937'
                                                    }}>
                                                        {getDayName(item.AvailableDate)}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '10px',
                                                        color: '#6b7280'
                                                    }}>
                                                        {item.AvailableDate ? new Date(item.AvailableDate).toLocaleDateString('en-GB') : 'N/A'}
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{
                                                width: '18px',
                                                height: '18px',
                                                borderRadius: '4px',
                                                border: item.IsAvailable ? '2px solid #3b82f6' : '2px solid #d1d5db',
                                                backgroundColor: item.IsAvailable ? '#3b82f6' : 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.2s ease'
                                            }}>
                                                {item.IsAvailable && (
                                                    <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '60px 20px',
                                    color: '#9ca3af'
                                }}>
                                    <p style={{ fontSize: '13px', margin: 0 }}>
                                        Please select a doctor and date range to view availability
                                    </p>
                                </div>
                            )}
                        </div>
                    </Paper>

                    {/* Messages */}
                    {errorMessage && (
                        <Paper elevation={2} style={{
                            marginTop: '15px',
                            padding: '12px 20px',
                            backgroundColor: '#fee2e2',
                            borderRadius: '8px',
                            border: '1px solid #fca5a5'
                        }}>
                            <p style={{ color: '#dc2626', fontWeight: '500', fontSize: '13px', margin: 0 }}>
                                ⚠️ {errorMessage}
                            </p>
                        </Paper>
                    )}

                    {successMessage && (
                        <Paper elevation={2} style={{
                            marginTop: '15px',
                            padding: '12px 20px',
                            backgroundColor: '#d1fae5',
                            borderRadius: '8px',
                            border: '1px solid #6ee7b7'
                        }}>
                            <p style={{ color: '#059669', fontWeight: '500', fontSize: '13px', margin: 0 }}>
                                ✓ {successMessage}
                            </p>
                        </Paper>
                    )}
                </div>
            </div>
        );
    };

    return (
        <CustomTableBody daysOfMonth={daysOfMonth} setDaysOfMonth={setDaysOfMonth} />
    );
}

export default DoctorMontlyDiary;
